
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, Plus, Sparkles, ArrowRight, BookOpen, Users } from "lucide-react";
import CreateProjectDialog from "@/components/Auth/Projects/CreateProjectDialog";
import SharedWithYouSection from "./SharedWithYouSection";

interface NewUserWelcomeProps {
  onCreateProject: (name: string) => Promise<void>;
  onCreateSampleProject: () => Promise<void>;
  onProjectSelect?: (projectId: string) => void;
  onFolderSelect?: (folderId: string) => void;
}

const NewUserWelcome: React.FC<NewUserWelcomeProps> = ({ 
  onCreateProject, 
  onCreateSampleProject,
  onProjectSelect,
  onFolderSelect
}) => {
  const [isCreateProjectDialogOpen, setIsCreateProjectDialogOpen] = useState(false);
  const [isCreatingSample, setIsCreatingSample] = useState(false);

  const handleCreateProject = async (name: string) => {
    await onCreateProject(name);
    setIsCreateProjectDialogOpen(false);
  };

  const handleCreateSampleProject = async () => {
    setIsCreatingSample(true);
    try {
      await onCreateSampleProject();
    } finally {
      setIsCreatingSample(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <main className="flex-1 p-4 sm:p-6 max-w-4xl mx-auto w-full">
        {/* Shared Items Section */}
        <div className="mb-12">
          <SharedWithYouSection 
            onProjectSelect={onProjectSelect}
            onFolderSelect={onFolderSelect}
          />
        </div>

        <div className="flex flex-col items-center justify-center text-center py-12 space-y-8">
          {/* Hero Section */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-primary/10 to-primary/20 p-8 rounded-full mb-6 inline-block">
              <Building2 className="h-16 w-16 text-primary" />
            </div>
            
            <div className="space-y-4">
              <h1 className="text-3xl font-bold text-slate-800 sm:text-4xl">
                Välkommen till Arbetsmiljökontrollen!
              </h1>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                För att komma igång behöver du skapa ditt första projekt. 
                Ett projekt hjälper dig organisera mappar och riskbedömningar för dina byggprojekt.
              </p>
            </div>
          </div>

          {/* Action Cards */}
          <div className="grid gap-6 md:grid-cols-2 w-full max-w-2xl">
            <Card className="border-primary/20 hover:border-primary/40 transition-colors">
              <CardHeader className="text-center">
                <div className="mx-auto mb-2 p-3 bg-primary/10 rounded-full w-fit">
                  <Plus className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg">Skapa eget projekt</CardTitle>
                <CardDescription>
                  Börja från grunden med ditt eget projektnamn och struktur
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  onClick={() => setIsCreateProjectDialogOpen(true)}
                  className="w-full"
                  size="lg"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Skapa projekt
                </Button>
              </CardContent>
            </Card>

            <Card className="border-green-200 hover:border-green-300 transition-colors">
              <CardHeader className="text-center">
                <div className="mx-auto mb-2 p-3 bg-green-100 rounded-full w-fit">
                  <Sparkles className="h-6 w-6 text-green-600" />
                </div>
                <CardTitle className="text-lg">Exempelprojekt</CardTitle>
                <CardDescription>
                  Utforska funktioner med ett färdigt projekt som innehåller mappar och exempel
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  onClick={handleCreateSampleProject}
                  disabled={isCreatingSample}
                  variant="outline"
                  className="w-full border-green-200 text-green-700 hover:bg-green-50"
                  size="lg"
                >
                  {isCreatingSample ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-2"></div>
                      Skapar...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Skapa exempelprojekt
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Info Section */}
          <div className="mt-12 p-6 bg-slate-50 rounded-lg border border-slate-200 max-w-2xl">
            <h3 className="font-semibold text-slate-800 mb-3 flex items-center">
              <BookOpen className="h-5 w-5 mr-2 text-primary" />
              Vad händer sedan?
            </h3>
            <div className="space-y-3 text-sm text-slate-600">
              <div className="flex items-start">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-white text-xs mr-3 mt-0.5">1</div>
                <p>Du skapar mappar för att organisera dina riskbedömningar</p>
              </div>
              <div className="flex items-start">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-white text-xs mr-3 mt-0.5">2</div>
                <p>Du börjar skapa risksammanställningar i dina mappar</p>
              </div>
              <div className="flex items-start">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-white text-xs mr-3 mt-0.5">3</div>
                <p>Du kan dela projekt med kollegor för samarbete</p>
              </div>
            </div>
          </div>

          {/* Support Info */}
          <div className="text-center text-sm text-slate-500">
            <p className="flex items-center justify-center">
              <Users className="h-4 w-4 mr-1" />
              Behöver du hjälp? Kontakta support eller läs vår dokumentation.
            </p>
          </div>
        </div>

        <CreateProjectDialog
          isOpen={isCreateProjectDialogOpen}
          onOpenChange={setIsCreateProjectDialogOpen}
          onCreateProject={handleCreateProject}
        />
      </main>
    </div>
  );
};

export default NewUserWelcome;
